'use client';

import {
  createFormTemplateKey,
  IFormTemplateKey,
  IFormTemplateKeyCreate,
  updateFormTemplateKey,
} from '@commenda/api';
import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Textarea,
} from '@commenda/kakashi';
import { useEffect, useState } from 'react';
import { useNotification } from '../../hooks/useNotification';
import { useFormEditor } from '../hooks/useFormEditor';

interface CreateKeyModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: (createdKey: IFormTemplateKeyCreate) => void;
  editingKey?: IFormTemplateKey | null;
}

export function CreateKeyModal({
  open,
  onOpenChange,
  onSuccess,
  editingKey,
}: CreateKeyModalProps) {
  const { setNotification } = useNotification();
  const { devMode } = useFormEditor();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [liveFormId, setLiveFormId] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Reset form when modal opens/closes or editingKey changes
  useEffect(() => {
    if (!open) return;

    if (editingKey) {
      setName(editingKey.name);
      setDescription(editingKey.description || '');
      setLiveFormId(editingKey.liveFormId || '');
    } else {
      setName('');
      setDescription('');
      setLiveFormId('');
    }
  }, [open, editingKey]);

  const handleSubmit = async () => {
    if (isSubmitting) return;

    if (!name.trim()) {
      setNotification({
        show: true,
        valid: false,
        title: 'Error',
        message: 'Please enter a key name',
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = {
        name: name.trim(),
        description: description.trim() || undefined,
        liveFormId: liveFormId.trim() || undefined,
      };

      if (editingKey) {
        const result = await updateFormTemplateKey(editingKey.id, payload);
        if (result.success) {
          onOpenChange(false);
          setName('');
          setDescription('');
          setLiveFormId('');
          setNotification({
            show: true,
            valid: true,
            title: 'Success',
            message: 'Template key updated successfully.',
          });
        } else {
          setNotification({
            show: true,
            valid: false,
            title: 'Error',
            message: 'Failed to update key. Please try again.',
          });
        }
      } else {
        const result = await createFormTemplateKey(payload);
        if (result.success && result.data) {
          onSuccess?.(result.data);
          onOpenChange(false);
          setName('');
          setDescription('');
          setLiveFormId('');
          setNotification({
            show: true,
            valid: true,
            title: 'Success',
            message: 'Template key created successfully.',
          });
        } else {
          setNotification({
            show: true,
            valid: false,
            title: 'Error',
            message: 'Failed to create key. Please try again.',
          });
        }
      }
    } catch (error) {
      console.error('Error saving key:', error);
      setNotification({
        show: true,
        valid: false,
        title: 'Error',
        message: `Failed to ${editingKey ? 'update' : 'create'} key. Please try again.`,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const actionLabel = editingKey ? 'Update' : 'Create';

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader className="flex flex-col gap-xs justify-start items-start">
          <DialogTitle>
            {editingKey ? 'Edit Template Key' : 'Create Template Key'}
          </DialogTitle>
          <DialogDescription>
            {editingKey
              ? 'Update the template key details.'
              : 'Create a new template key to organize your form templates.'}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4 px-4">
          <div className="space-y-2">
            <Label htmlFor="keyName">
              Name <span className="text-text-destructive">*</span>
            </Label>
            <Input
              id="keyName"
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter key name"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="keyDescription">Description</Label>
            <Textarea
              id="keyDescription"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Enter key description"
              rows={3}
            />
          </div>
          {devMode && (
            <div className="space-y-2">
              <Label htmlFor="liveFormId">Live Form ID</Label>
              <Input
                id="liveFormId"
                value={liveFormId}
                onChange={(e) => setLiveFormId(e.target.value)}
                placeholder="Enter live form ID (optional)"
              />
            </div>
          )}
        </div>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="secondary">Cancel</Button>
          </DialogClose>
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={isSubmitting || !name.trim()}
          >
            {isSubmitting ? `${actionLabel}ing...` : `${actionLabel} Key`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
